import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import { EnterpriseContext } from "./EnterpriseContext";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import { confirmWrapper } from "../ui/askConfirm";
import truckyService from "../../common/services/trucky-service";

function SingleQuestion({ container, question, refreshQuestions }) {
  const [questionData, setQuestionData] = useState(question);
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const company = globalContext.company;

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }

  const deleteQuestion = async (questionId) => {
    if (
      await confirmWrapper(container.localeManager.strings.areYouSure, {
        container: container,
      })
    ) {
      const result = await es.deleteCompanyQuestion(company.id, questionId);
      if (result.error) {
        toast.error(result.message);
      } else {
        refreshQuestions();
      }
    }
  };

  const updateQuestion = async (question) => {
    const result = await es.updateCompanyQuestion(company.id, question);
    if (result.error) {
      toast.error(result.message);
    } else {
      refreshQuestions();
      toast.success(container.localeManager.strings.dataUpdated);
    }
  };

  return (
    <Card className="mb-3 info-card">
      <CardBody>
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="page-title-icon page-title-icon-primary page-title-icon-sm">
            <FontAwesomeIcon icon="circle-question" className="mx-0" />
          </span>
          <span className="fw-bold">#{question.order ?? ''} — {question.question}</span>
          {question.is_required && (
            <span className="badge bg-danger ms-auto">{container.localeManager.strings.required}</span>
          )}
        </div>
        <Row className="g-2">
          <Col sm={8}>
            <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.question}</Label>
            <Input
              type="textarea"
              rows={2}
              value={questionData.question}
              onChange={(e) => {
                questionData.question = e.target.value;
                setQuestionData({ ...questionData });
              }}
            />
          </Col>
          <Col sm={2}>
            <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.questionType}</Label>
            <Input
              type="select"
              value={questionData.type}
              onChange={(e) => {
                questionData.type = e.target.value;
                setQuestionData({ ...questionData });
              }}
            >
              <option value="text">{container.localeManager.strings.shortText}</option>
              <option value="textarea">{container.localeManager.strings.longText}</option>
              <option value="select">{container.localeManager.strings.dropdown}</option>
              <option value="checkbox">{container.localeManager.strings.checkbox}</option>
              <option value="radio">{container.localeManager.strings.radio}</option>
            </Input>
          </Col>
          <Col sm={2}>
            <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.required}</Label>
            <div className="mt-1">
              <input
                onChange={(e) =>
                  setQuestionData({
                    ...questionData,
                    is_required: e.target.checked,
                  })
                }
                className="form-check-input"
                type="checkbox"
                defaultChecked={questionData.is_required}
              />
            </div>
          </Col>
        </Row>
        {(questionData.type === "select" || questionData.type === "radio") && (
          <Row className="g-2 mt-1">
            <Col sm={12}>
              <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.questionOptions}</Label>
              <Input
                type="textarea"
                rows={2}
                value={questionData.options || ""}
                onChange={(e) => {
                  questionData.options = e.target.value;
                  setQuestionData({ ...questionData });
                }}
              />
            </Col>
          </Row>
        )}
        <Row className="g-2 mt-1">
          <Col sm={2}>
            <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.order}</Label>
            <Input
              type="number"
              min="0"
              value={questionData.order ?? ''}
              onChange={(e) =>
                setQuestionData({
                  ...questionData,
                  order: e.target.value,
                })
              }
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button color="primary" size="sm" onClick={() => updateQuestion(questionData)}>
            <FontAwesomeIcon icon="check" className="me-1" />{container.localeManager.strings.save}
          </Button>
          <Button color="danger" size="sm" onClick={() => deleteQuestion(questionData.id)}>
            <FontAwesomeIcon icon="trash" className="me-1" />{container.localeManager.strings.delete}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function ApplicationQuestionnaireManagement({ container }) {
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const [questions, setQuestions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const company = globalContext.company;
  const [newQuestion, setNewQuestion] = useState({
    question: null,
    type: "text",
    options: null, // comma separated options for radio/checkbox questions
  });

  useEffect(() => {
    truckyService.setDocumentTitle("Questionnaire");
  }, []);

  useEffect(() => {
    getQuestions();
  }, []);

  const getQuestions = async () => {
    setLoadingData(true);
    const result = await es.getCompanyQuestions(company.id);
    if (result.error) {
      toast.error(result.message);
    } else {
      setQuestions(result);
    }
    setLoadingData(false);
  };

  const addQuestion = async () => {
    setLoadingData(true);
    const result = await es.createCompanyQuestion(company.id, newQuestion);
    if (result.error) {
      toast.error(result.message);
    } else {
      getQuestions();
    }
    setLoadingData(false);
    setNewQuestion({
      question: null,
      type: "text",
      options: null,
    });
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        to: "/applications",
                        title: container.localeManager.strings.applications,
                      },
                      {
                        title: container.localeManager.strings.questionnaire,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="page-hero mb-4">
                    <span className="page-hero-icon">
                      <FontAwesomeIcon icon="list-check" className="mx-0" />
                    </span>
                    <div className="flex-1">
                      <h1 className="page-hero-title mb-0">{container.localeManager.strings.questionnaire}</h1>
                    </div>
                  </div>
                  <div className="info-card p-4 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="page-title-icon page-title-icon-primary page-title-icon-sm">
                        <FontAwesomeIcon icon="plus" className="mx-0" />
                      </span>
                      <span className="fw-bold">{container.localeManager.strings.addQuestion}</span>
                    </div>
                    <Row className="g-2">
                      <Col>
                        <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.question}</Label>
                        <Input
                          type="textarea"
                          rows={2}
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              question: e.target.value,
                            })
                          }
                          value={newQuestion.question || ""}
                        />
                      </Col>
                      <Col>
                        <Label className="fw-semibold small text-uppercase text-muted">
                          {container.localeManager.strings.questionType}
                        </Label>
                        <Input
                          type="select"
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              type: e.target.value,
                            })
                          }
                          value={newQuestion.type || ""}
                        >
                          <option value="">{ container.localeManager.strings.selectType}</option>
                          <option value="text">{container.localeManager.strings.shortText}</option>
                          <option value="textarea">{container.localeManager.strings.longText}</option>
                          <option value="select">{container.localeManager.strings.dropdown}</option>
                          <option value="checkbox">{container.localeManager.strings.checkbox}</option>
                          <option value="radio">{container.localeManager.strings.radio}</option>
                        </Input>
                      </Col>
                      <Col sm={2}>
                        <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.required}</Label>
                        <div className="mt-1">
                          <input
                            onChange={(e) =>
                              setNewQuestion({
                                ...newQuestion,
                                is_required: e.target.checked,
                              })
                            }
                            className="form-check-input"
                            type="checkbox"
                          />
                        </div>
                      </Col>
                    </Row>
                    {(newQuestion.type === "select" || newQuestion.type === "radio") && (
                      <Row className="g-2 mt-1">
                        <Col sm={6}>
                          <Label className="fw-semibold small text-uppercase text-muted">
                            {container.localeManager.strings.questionOptions}
                          </Label>
                          <Input
                            type="textarea"
                            rows={2}
                            onChange={(e) =>
                              setNewQuestion({
                                ...newQuestion,
                                options: e.target.value,
                              })
                            }
                            value={newQuestion.options || ""}
                          />
                        </Col>
                      </Row>
                    )}
                    <Row className="g-2 mt-1">
                      <Col sm={2}>
                        <Label className="fw-semibold small text-uppercase text-muted">{container.localeManager.strings.order}</Label>
                        <Input
                          type="number"
                          min="0"
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              order: e.target.value,
                            })
                          }
                          value={newQuestion.order || ""}
                        />
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <Button
                        color="primary"
                        onClick={addQuestion}
                        disabled={loadingData}
                        size="sm"
                      >
                        <FontAwesomeIcon icon="plus" className="me-1" />
                        {container.localeManager.strings.addQuestion}
                      </Button>
                    </div>
                  </div>
                  {questions.length > 0 && (
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="page-title-icon page-title-icon-info page-title-icon-sm">
                        <FontAwesomeIcon icon="list-check" className="mx-0" />
                      </span>
                      <span className="fw-bold">{questions.length} {questions.length === 1 ? 'Question' : 'Questions'}</span>
                    </div>
                  )}
                </CardBody>
                {questions.map((question) => (
                  <SingleQuestion
                    key={question.id}
                    container={container}
                    question={question}
                    refreshQuestions={getQuestions}
                  />
                ))}
              </Card>
            </Col>
          </Row>
        </Col>
        <aside className="ad-column-placeholder">
          <BoxDynamicAd container={container} className="my-3" />
        </aside>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
}
